import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../services/api';
import BackButton from '../components/BackButton';
import { formatDate } from '../utils/dateFormatter';
import { getDaysRemaining } from '../utils/gameUtils';
import { Game, GameStatus } from '@bst-stellar/shared';

export default function MyGamesPage() {
    const api = useApi();

    const [games, setGames] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState<'active' | 'ended'>('active');

    useEffect(() => {
        loadGames();
    }, []);

    async function loadGames() {
        try {
            setLoading(true);
            const result = await api.getGames();
            setGames(result.games);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl">Loading games...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-2">Error</h1>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    const activeGames = games.filter((g) => g.status === GameStatus.ACTIVE);
    const endedGames = games.filter((g) => g.status !== GameStatus.ACTIVE);
    const displayedGames = tab === 'active' ? activeGames : endedGames;

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto py-6 px-4">
                <div className="flex flex-col gap-2 mb-6">
                    <BackButton />
                    <h1 className="text-3xl font-bold text-text-primary">My Games</h1>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-surface rounded-lg p-1 border border-border w-fit">
                    <button
                        onClick={() => setTab('active')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                            tab === 'active'
                                ? 'bg-primary text-white shadow'
                                : 'text-text-secondary hover:text-text-primary'
                        }`}
                    >
                        Active ({activeGames.length})
                    </button>
                    <button
                        onClick={() => setTab('ended')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                            tab === 'ended'
                                ? 'bg-primary text-white shadow'
                                : 'text-text-secondary hover:text-text-primary'
                        }`}
                    >
                        Ended ({endedGames.length})
                    </button>
                </div>

                {/* Games Grid */}
                {displayedGames.length === 0 ? (
                    <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                        <p className="text-text-secondary text-lg">
                            {tab === 'active' ? 'No active games right now.' : 'No ended games yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayedGames.map((game) => (
                            <Link
                                key={game.id}
                                to={`/games/${game.id}`}
                                className={`block bg-surface rounded-lg shadow hover:shadow-md transition border border-border ${
                                    tab === 'ended' ? 'opacity-75' : ''
                                }`}
                            >
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-xl font-bold text-text-primary">{game.name}</h3>
                                        {tab === 'active' ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                Ended
                                            </span>
                                        )}
                                    </div>
                                    {game.description && (
                                        <p className="text-text-secondary mb-4 line-clamp-2">
                                            {game.description}
                                        </p>
                                    )}
                                    <div className="text-sm text-text-secondary">
                                        {tab === 'active' ? (() => {
                                            const days = getDaysRemaining(game.endDate);
                                            if (days === null) return null;
                                            if (days === 0) return <p className="font-semibold text-amber-600">⏰ Ends today!</p>;
                                            return <p className="font-semibold text-primary">⏳ {days} day{days !== 1 ? 's' : ''} left</p>;
                                        })() : (
                                            <p>Ended on: {formatDate(game.endDate)}</p>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {games.length === 0 && (
                    <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                        <p className="text-gray-500 text-lg">No games available yet</p>
                        <p className="text-gray-400 text-sm mt-2">
                            Check back later or contact your admin
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
